import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { trackSignUp } from "@/lib/gtm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Compass, Mail, Lock, User as UserIcon, CheckCircle2, Check, X as XIcon, Sparkles } from "lucide-react";
import { evaluatePasswordStrength } from "@/lib/passwordStrength";
import { TERMS_VERSION, PRIVACY_VERSION } from "@/pages/Auth";

// Standalone sign-up page for admin-generated role invites. The route lives
// at /role-invite/:token to keep it fully separate from the existing
// /register (regular sign-up), /invite/:token (second-parent invite), and
// /bundle-* flows. Nothing is shared with those pages beyond the two
// constants (TERMS_VERSION, PRIVACY_VERSION) and the shadcn primitives.
//
// Lifecycle:
// 1. On mount, hit GET /role-invites/:token to resolve the invite's role,
//    status, and expiry. Render an error card for invalid / used / revoked /
//    expired tokens.
// 2. Collect name + email + password + T&C/Privacy consent (both unticked
//    by default; T&C and Privacy links open inline Dialogs with the policy
//    copy so the signup state is preserved on mobile — mirrors the P0 fix
//    already shipped on /register).
// 3. POST /role-invites/:token/accept. On success the server has created the
//    user + sent the verification email + marked the invite `used`. Route
//    the invitee to the login page with their email pre-filled so they can
//    sign in after confirming.
export default function InviteSignUp() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({ first_name: "", surname: "", email: "", password: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null); // "terms" | "privacy" | null
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/role-invites/${token}`);
        if (cancelled) return;
        if (data.status === "active") {
          setInvite(data);
          setStatus("ready");
        } else {
          setErrorMsg(
            data.status === "used" ? "This invite has already been used."
              : data.status === "revoked" ? "This invite has been revoked by the admin."
              : data.status === "expired" ? "This invite has expired. Please ask the admin for a new one."
              : "This invite is no longer valid."
          );
          setInvite(data);
          setStatus("error");
        }
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(errMsg(e, "This invite link is not valid."));
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreeTerms || !agreePrivacy) return;
    if (!form.password || form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/role-invites/${token}/accept`, {
        first_name: form.first_name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
      });
      trackSignUp(invite?.role, "role_invite");
      toast.success("Account created — please check your email to confirm.");
      setDone(true);
      // Redirect to the login page with the email pre-filled after a short
      // beat so the user sees the success state first.
      setTimeout(() => navigate(`/login?email=${encodeURIComponent(form.email.trim().toLowerCase())}`), 1800);
    } catch (err) {
      toast.error(errMsg(err, "Could not accept invite"));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-signup-loading">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 text-center text-forest/60">
          Checking your invite link…
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-signup-error">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 max-w-md w-full shadow-sm fade-in text-center">
          <div className="w-14 h-14 rounded-full bg-terracotta-light flex items-center justify-center mx-auto mb-4">
            <XIcon className="w-7 h-7 text-terracotta-dark" />
          </div>
          <h1 className="text-xl font-black text-forest mb-2" data-testid="invite-signup-error-title">Invite not available</h1>
          <p className="text-sm text-forest/70 mb-6" data-testid="invite-signup-error-message">{errorMsg}</p>
          <Link to="/">
            <Button className="rounded-full bg-sage hover:bg-sage-dark text-white w-full" data-testid="invite-signup-home-btn">Back to home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-signup-done">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 max-w-md w-full shadow-sm fade-in text-center">
          <div className="w-14 h-14 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-sage-dark" />
          </div>
          <h1 className="text-xl font-black text-forest mb-2">Check your inbox</h1>
          <p className="text-sm text-forest/70">We've sent a confirmation email to <strong>{form.email}</strong>. Open it to verify your account, then sign in.</p>
        </div>
      </div>
    );
  }

  const roleLabel = invite?.role_label || "member";
  const strength = form.password ? evaluatePasswordStrength(form.password) : null;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="invite-signup-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" />
          </span>
          <div>
            <h1 className="text-xl font-black text-forest leading-tight">You've been invited</h1>
            <p className="text-xs text-forest/60">Sign up as a <strong>{roleLabel}</strong></p>
          </div>
        </div>

        <div className="rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/30 p-4 mb-5 flex items-start gap-3" data-testid="invite-signup-role-banner">
          <Sparkles className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
          <div className="text-sm text-forest/80">
            Compass Kids has invited you to join as a <strong className="text-terracotta-dark">{roleLabel}</strong>. Complete the quick sign-up below — admin approval and payment come next, just like any new {roleLabel}.
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first-name" className="text-forest/80">First name</Label>
              <div className="relative mt-1">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
                <Input id="first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="Jane" data-testid="invite-first-name-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="surname" className="text-forest/80">Surname</Label>
              <Input id="surname" required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
                className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Doe" data-testid="invite-surname-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="email" className="text-forest/80">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="you@example.com" data-testid="invite-email-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-forest/80">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="At least 6 characters" data-testid="invite-password-input" />
            </div>
            {strength && (
              <div className="mt-1.5" data-testid="invite-password-strength">
                <div className="h-1.5 w-full bg-sage-light/40 rounded-full overflow-hidden">
                  <div className={`h-full ${strength.color} transition-all`} style={{ width: `${strength.score}%` }} />
                </div>
                <p className="text-[11px] text-forest/60 mt-1">Strength: <span className="font-bold">{strength.label}</span></p>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-forest/80">Confirm password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
              <Input id="confirm-password" type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-9 rounded-xl bg-softstone/60 border-sage/30" placeholder="Re-type your password" data-testid="invite-confirm-password-input" />
            </div>
            {confirmPassword && (
              form.password === confirmPassword ? (
                <p className="text-[11px] text-sage-dark mt-1 flex items-center gap-1" data-testid="invite-password-match">
                  <Check className="w-3 h-3" /> Passwords match
                </p>
              ) : (
                <p className="text-[11px] text-terracotta-dark mt-1 flex items-center gap-1" data-testid="invite-password-mismatch">
                  <XIcon className="w-3 h-3" /> Passwords do not match
                </p>
              )
            )}
          </div>

          <div className="space-y-3 rounded-xl bg-terracotta-light/60 border-2 border-terracotta/40 p-4" data-testid="invite-consent-block">
            <p className="text-xs font-bold text-terracotta-dark uppercase tracking-wide">
              Required — please tick both to continue
            </p>
            <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                data-testid="invite-agree-terms-checkbox"
              />
              <span>
                I agree to the{" "}
                <button type="button" onClick={() => setLegalOpen("terms")} className="font-bold text-terracotta-dark underline" data-testid="invite-terms-link">
                  Terms &amp; Conditions
                </button>.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-forest cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                data-testid="invite-agree-privacy-checkbox"
              />
              <span>
                I agree to the{" "}
                <button type="button" onClick={() => setLegalOpen("privacy")} className="font-bold text-terracotta-dark underline" data-testid="invite-privacy-link">
                  Privacy Policy
                </button>.
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={submitting || !agreeTerms || !agreePrivacy || !form.first_name || !form.surname || !form.email || !form.password || form.password !== confirmPassword}
            className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md"
            data-testid="invite-submit-btn"
          >
            {submitting ? "Creating account…" : `Sign Up as ${roleLabel}`}
          </Button>
        </form>

        <p className="text-center text-sm text-forest/60 mt-6">
          Already a member? <Link to="/login" className="font-bold text-terracotta-dark hover:underline">Sign in</Link>
        </p>
      </div>

      {/* Inline legal dialog — identical pattern to Auth.jsx so form state is
          preserved on mobile instead of opening /terms in a new tab. */}
      <Dialog open={legalOpen !== null} onOpenChange={(open) => !open && setLegalOpen(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`invite-legal-dialog-${legalOpen || "closed"}`}>
          <DialogHeader>
            <DialogTitle>{legalOpen === "terms" ? "Terms & Conditions" : "Privacy Policy"}</DialogTitle>
            <DialogDescription>
              {legalOpen === "terms"
                ? `Version ${TERMS_VERSION} · Please read before accepting.`
                : `Version ${PRIVACY_VERSION} · Please read before accepting.`}
            </DialogDescription>
          </DialogHeader>
          {legalOpen === "terms" ? (
            <div className="space-y-3 text-sm text-forest/80 leading-relaxed" data-testid="invite-legal-dialog-terms-body">
              <p>Welcome to Compass Kids. By creating an account you agree to use the platform respectfully, keep your login details private, and share accurate information about yourself and (where applicable) your business.</p>
              <h3 className="font-black text-forest">Your account</h3>
              <p>Each role is a separate account under your email, with its own verification, admin approval and (where applicable) subscription. You remain responsible for the activity that happens on your account.</p>
              <h3 className="font-black text-forest">Community conduct</h3>
              <p>Treat other families, hosts, vendors and admins with kindness. Harassment, discrimination, spam, or any content that puts children at risk will result in immediate removal from the platform.</p>
              <h3 className="font-black text-forest">Payments &amp; subscriptions</h3>
              <p>Vendor, Event Creator and Marketplace memberships are billed monthly via Paystack. You can cancel from your portal at any time; your access continues until the end of the current period.</p>
            </div>
          ) : (
            <div className="space-y-3 text-sm text-forest/80 leading-relaxed" data-testid="invite-legal-dialog-privacy-body">
              <p>Compass Kids collects only what we need to run a safe, family-friendly community platform. We never sell your personal information.</p>
              <h3 className="font-black text-forest">What we collect</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Account details (name, email, hashed password).</li>
                <li>Role-specific profile data you choose to provide.</li>
                <li>Payment metadata from Paystack (we never store your card number).</li>
              </ul>
              <h3 className="font-black text-forest">Your rights</h3>
              <p>You can download, correct or permanently delete your account data from your profile page at any time.</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                onClick={() => {
                  if (legalOpen === "terms") setAgreeTerms(true);
                  else if (legalOpen === "privacy") setAgreePrivacy(true);
                  setLegalOpen(null);
                }}
                className="w-full rounded-full bg-sage hover:bg-sage-dark text-white"
                data-testid="invite-legal-dialog-accept-btn"
              >
                I understand &amp; accept
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="w-full rounded-full" data-testid="invite-legal-dialog-close-btn">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
